import React from 'react';

interface Props {
    text?: string;
    children?: React.ReactNode;
}

export const WindowTitleDepthContext = React.createContext<number>(0);

interface Entry {
    depth: number;
    text: string;
}

const baseTitle = document.title;
const entries = new Map<number, Entry>();

function applyTitle() {
    let deepest: Entry | undefined;
    for (const entry of entries.values()) {
        if (!deepest || entry.depth >= deepest.depth) {
            deepest = entry;
        }
    }
    document.title = deepest ? `${deepest.text} – ${baseTitle}` : baseTitle;
}

function registerWindowTitle(id: number, depth: number, text: string) {
    entries.set(id, {depth, text});
    applyTitle();
}

function unregisterWindowTitle(id: number) {
    entries.delete(id);
    applyTitle();
}

let nextId = 0;

// sets window title. if multiple instances are rendered in the tree, the deepest one wins
export const BrowserTabTitle: React.FunctionComponent<Props> = ({text, children}) => {
    const depth = React.useContext(WindowTitleDepthContext) + 1;
    const idRef = React.useRef<number | undefined>(undefined);
    if (idRef.current === undefined) {
        idRef.current = nextId++;
    }
    const id = idRef.current;

    React.useEffect(() => {
        if (text === undefined) {
            return undefined;
        }
        registerWindowTitle(id, depth, text);
        return () => unregisterWindowTitle(id);
    }, [id, depth, text]);

    return (
        <WindowTitleDepthContext.Provider value={depth}>
            {children}
        </WindowTitleDepthContext.Provider>
    );
};
